// src/pages/home/HomePage.jsx
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "react-toastify";
import { notiService } from "../../../services/api/notiService";
import { serviceService } from "../../../services/api/serviceService";
import { bannerService } from "../../../services/api/bannerService";
import CategoryGrid from "./components/CategoryGrid";
import NavigationGrid from "./components/NavigationGrid";
import SideMenu from "./components/SideMenu";
import HomeMobileHeader from "./components/HomeMobileHeader";

// SwiperJS
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { motion, AnimatePresence } from "framer-motion";

/* ---------------- utils ---------------- */
const toSlug = (str) =>
  str
    .replace(/Đ/g, "D")
    .replace(/đ/g, "d")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const findService = (response) => {
  const serviceList = [];
  if (Array.isArray(response)) {
    response.forEach((item) => {
      if (!serviceList.find((cat) => cat.id === item.id)) {
        serviceList.push({
          id: item.id,
          name: item.name,
          formName: item.formName,
          submissionTime: item.submissionTime,
          reviewTime: item.reviewTime,
        });
      }
    });
  }
  return serviceList;
};

const truncateUsername = (username) =>
  username
    ? username.length > 15
      ? `${username.slice(0, 15)}...`
      : username
    : "";

const formatNoti = (data) =>
  data.map((item) => ({
    ...item,
    type: "notification",
    icon: "/images/notification-icon.png",
    note: item.message.replace(item.profileId, ``),
    profileId: item.profileId,
  }));

const findCategory = (response) => {
  const categoryList = [];
  if (Array.isArray(response)) {
    response.forEach((item) => {
      if (!categoryList.find((cat) => cat.id === item.id)) {
        categoryList.push({ id: item.id, name: item.name, image: item.image });
      }
    });
  }
  return categoryList;
};

/* --------------- motion variants --------------- */
const drawerBackdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};
const drawerPanel = {
  hidden: { x: "100%" },
  show: {
    x: 0,
    transition: { type: "spring", stiffness: 380, damping: 36 },
  },
  exit: { x: "100%", transition: { duration: 0.18 } },
};

// Thay đổi panel: trượt theo trục Y từ đáy
const bottomSheetPanel = {
  hidden: { y: "100%" },
  show: {
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 40, mass: 0.8 },
  },
  exit: { y: "100%", transition: { duration: 0.18 } },
};
/* ---------------- component ---------------- */
const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user_client = {}, token_client } = useContext(AuthContext);

  // UI state
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Notifications
  const [noti, setNoti] = useState([]);
  const [page, setPage] = useState(1);
  const [notiNum, setNotiNum] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Categories & Services
  const [category, setCategory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [search, setSearch] = useState(false);

  // Banners
  const [banners, setBanners] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [isHomeHeaderCollapsed, setIsHomeHeaderCollapsed] = useState(false);
  const contentScrollTopRef = useRef(0);
  const isHomeHeaderCollapsedRef = useRef(false);

  const updateHomeHeaderCollapsed = useCallback((nextValue) => {
    if (isHomeHeaderCollapsedRef.current === nextValue) return;
    isHomeHeaderCollapsedRef.current = nextValue;
    setIsHomeHeaderCollapsed(nextValue);
  }, []);

  const handleHomeContentScroll = useCallback(
    (event) => {
      const nextTop = event.currentTarget.scrollTop;
      const prevTop = contentScrollTopRef.current;

      if (nextTop <= 8) {
        updateHomeHeaderCollapsed(false);
      } else if (nextTop > 32 && nextTop > prevTop + 6) {
        updateHomeHeaderCollapsed(true);
      } else if (nextTop < prevTop - 10) {
        updateHomeHeaderCollapsed(false);
      }

      contentScrollTopRef.current = nextTop;
    },
    [updateHomeHeaderCollapsed],
  );

  /* ---------------- notifications ---------------- */
  const fetchNoti = useCallback(async () => {
    try {
      const response = await notiService.get_all(token_client, page);
      const newNoti = formatNoti(response.data);
      setNoti((prevNoti) => [...prevNoti, ...newNoti]);
      setPage((prevPage) => prevPage + 1);
      setHasMore(
        response.pagination.currentPage < response.pagination.totalPages
      );
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi tải thông báo");
      console.error("Fetch notification error:", error);
    }
  }, [token_client, page]);

  const fetchNotiCount = useCallback(async () => {
    try {
      const response = await notiService.get_count(token_client);
      setNotiNum(response.newNotiCount);
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi tải số thông báo");
      console.error("Fetch notification count error:", error);
    }
  }, [token_client]);

  useEffect(() => {
    fetchNotiCount();
    const interval = setInterval(fetchNotiCount, 30000);
    return () => clearInterval(interval);
  }, [fetchNotiCount]);

  useEffect(() => {
    if (isNotificationOpen) {
      (async () => {
        try {
          const response = await notiService.get_all(token_client, 1);
          const firstNoti = formatNoti(response.data);
          setNotiNum(0);
          setNoti(firstNoti);
          setPage(2);
          setHasMore(
            response.pagination.currentPage < response.pagination.totalPages
          );
        } catch (error) {
          toast.dismiss();
          toast.error("Lỗi khi lấy thông báo từ server");
          console.error("Fetch notification error:", error);
        }
      })();
    }
  }, [isNotificationOpen, token_client]);

  const toggleNotification = () => setIsNotificationOpen((s) => !s);

  /* ---------------- categories & services ---------------- */
  const fetchCategory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await serviceService.get_all_category(token_client);
      const categories = findCategory(response.categories);
      setCategory(categories);

      const params = new URLSearchParams(location.search);
      const categorySlug = params.get("category");
      if (categorySlug) {
        const foundCategory = categories.find(
          (cat) => toSlug(cat.name) === categorySlug
        );
        setSelectedCategory(foundCategory || null);
      } else {
        setSelectedCategory(null);
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Lỗi khi lấy danh mục từ server");
    } finally {
      setLoading(false);
    }
  }, [token_client, location.search]);

  const fetchService = useCallback(
    async (category_id) => {
      if (search) return;
      setServiceLoading(true);
      setServices([]);
      try {
        const response = await serviceService.get_service_by_category(
          category_id,
          token_client
        );
        setServices(findService(response.services));
      } catch (error) {
        toast.dismiss();
        toast.error("Lỗi khi lấy dịch vụ từ server");
      } finally {
        setServiceLoading(false);
      }
    },
    [token_client, search]
  );

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  useEffect(() => {
    if (selectedCategory) fetchService(selectedCategory.id);
  }, [selectedCategory, fetchService]);

  /* ---------------- banners ---------------- */
  useEffect(() => {
    const fetchBanners = async () => {
      if (!token_client) return;
      setBannerLoading(true);
      try {
        const res = await bannerService.get_banner?.(token_client, {
          page: 1,
          limit: 5,
        });
        const items =
          res?.data?.data?.news ?? res?.data?.news ?? res?.news ?? [];
        setBanners(Array.isArray(items) ? items : []);
      } catch (err) {
        toast.dismiss();
        console.error("Fetch banners error:", err);
      } finally {
        setBannerLoading(false);
      }
    };
    fetchBanners();
  }, [token_client]);

  /* ---------------- render ---------------- */
  return (
    <div className="flex h-full flex-col overflow-hidden md:block md:h-auto md:overflow-visible">
      {/* Header */}
      <div
        className={[
          "shrink-0 overflow-hidden transition-[height,opacity,transform] duration-200 ease-out md:hidden",
          isHomeHeaderCollapsed
            ? "h-0 -translate-y-3 opacity-0"
            : "h-[132px] translate-y-0 opacity-100",
        ].join(" ")}
      >
        <HomeMobileHeader
          user={user_client}
          notificationCount={notiNum}
          onToggleNotification={toggleNotification}
          onOpenMenu={() => setIsMenuOpen(true)}
        />
      </div>

      <div className="mx-auto hidden w-full max-w-screen-lg items-center justify-between px-3 py-3 md:flex md:px-4 md:py-4">
        <div className="min-w-0 flex items-center gap-2 md:gap-3">
          <img
            src={user_client.avatar || "/images/avatar-default.png"}
            alt="user avatar"
            className="size-12 flex-shrink-0 rounded-full border border-[#1358CB] object-cover md:size-14"
            loading="lazy"
          />
          <div className="min-w-0 flex flex-col justify-center">
            <p
              className="truncate font-sf-medium text-sm uppercase text-[#1358CB] md:text-base"
              title={
                user_client.username ||
                user_client.fullName ||
                user_client.companyName
              }
            >
              {truncateUsername(
                user_client.username ||
                  user_client.fullName ||
                  user_client.companyName
              )}
            </p>
            <p className="font-be-vietnam-light text-[10px] text-[#1358CB] md:text-xs">
              {user_client.role === "Staff"
                ? "Nhân viên"
                : user_client.role === "Manager"
                ? "Quản lý"
                : "Cộng tác viên"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative">
            <button
              type="button"
              className="rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
              onClick={toggleNotification}
              aria-label="Mở thông báo"
            >
              <img src="/icons/ring.svg" alt="" className="size-6 md:size-7" />
            </button>
            {notiNum ? (
              <div className="absolute -top-0.5 right-0 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-red-600 text-[8px] text-white md:h-5 md:w-5 md:text-[10px]">
                {notiNum}
              </div>
            ) : null}
          </div>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
            aria-label="Mở menu"
          >
            <img src="/icons/menu.svg" alt="" className="size-6 md:size-7" />
          </button>
        </div>
      </div>

      <SideMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        user={user_client}
      />

      {/* Main */}
      <div
        onScroll={handleHomeContentScroll}
        className={[
          "relative z-10 flex-1 overflow-y-auto bg-white transition-[margin,border-radius,box-shadow] duration-200 ease-out md:mt-0 md:h-[calc(100%-100px)] md:min-h-0 md:overflow-auto md:rounded-none md:bg-[#F2F2F2] md:shadow-none",
          isHomeHeaderCollapsed
            ? "mt-0 rounded-none shadow-none"
            : "-mt-7 rounded-t-[32px] shadow-[0_-12px_28px_rgba(15,23,42,0.08)]",
        ].join(" ")}
      >
        <div className="mx-auto w-full px-4 pb-[calc(104px+env(safe-area-inset-bottom))] pt-8 md:px-4 md:pb-4 md:pt-3">
          <h1 className="my-1 w-full text-center font-be-vietnam-semiBold text-base text-[#007AFF] md:my-3 md:text-2xl">
            SOHUUTRITUE.VN
          </h1>

          {/* Banner Slider */}
          <div className="mb-3 md:mb-4">
            {bannerLoading ? (
              <div className="aspect-[16/9] w-full animate-pulse rounded-xl bg-gray-200 md:aspect-[16/7] md:rounded-2xl lg:aspect-[21/9]" />
            ) : banners.length > 0 ? (
              <Swiper
                modules={[Pagination, Autoplay]}
                slidesPerView={1}
                spaceBetween={10}
                loop={banners.length > 1}
                pagination={{ clickable: true }}
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                className="home-swiper overflow-hidden rounded-xl md:rounded-2xl"
                style={{ "--swiper-theme-color": "#ffffff" }}
                key={banners.length}
                onSwiper={(sw) => sw.autoplay?.start()}
                preventClicks
                preventClicksPropagation
              >
                {banners.map((banner) => (
                  <SwiperSlide
                    className="relative cursor-pointer"
                    key={banner._id}
                    onClick={() => navigate(`/banner/${banner._id}`)}
                  >
                    <img
                      src={banner.thumbnail}
                      alt={banner.name || "Banner"}
                      className="aspect-[16/9] w-full object-cover md:aspect-[16/7] lg:aspect-[21/9]"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = "/images/banner-fallback.jpg";
                      }}
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
                    <p className="absolute bottom-6 left-3 right-3 line-clamp-2 font-be-vietnam-semiBold text-[10px] leading-snug text-white drop-shadow sm:text-sm md:left-4 md:right-4 md:text-base">
                      {banner.name}
                    </p>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-3 rounded-xl bg-gray-200 md:aspect-[16/7] md:rounded-2xl lg:aspect-[21/9]">
                <img className="w-10 md:w-12" src="/images/empty.png" alt="" />
                <p className="font-be-vietnam-semiBold text-sm text-[#AEAEAE] md:text-base">
                  No banner
                </p>
              </div>
            )}
          </div>

          {/* Categories */}
          <h1 className="font-sf-bold text-[20px] text-[#1F1B3D] md:text-[18px] md:text-black">
            <span className="md:hidden">Nhóm dịch vụ</span>
            <span className="hidden md:inline">Dịch vụ</span>
          </h1>
          <CategoryGrid
            categories={category}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            data={services}
            setIsSearch={setSearch}
            serviceLoading={serviceLoading}
            loading={loading}
          />

          {/* Navigation */}
          <h1 className="mt-3 font-sf-bold text-[18px] md:mt-4">Chức năng</h1>
          <NavigationGrid />
        </div>
      </div>

      {/* Notification Drawer */}
      <AnimatePresence>
        {isNotificationOpen && (
          <>
            {/* Backdrop */}
            <motion.button
              aria-label="Đóng thông báo"
              className="fixed inset-0 z-40 bg-gray-900/50"
              variants={drawerBackdrop}
              initial="hidden"
              animate="show"
              exit="exit"
              onClick={toggleNotification}
            />

            {/* Bottom sheet */}
            <motion.div
              className="
          fixed inset-x-0 bottom-0 z-50
          w-full max-h-[85vh]
          rounded-t-2xl bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)]
          "
              variants={bottomSheetPanel}
              initial="hidden"
              animate="show"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-label="Thông báo"
              // Cho phép kéo xuống để đóng
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              onDragEnd={(_e, info) => {
                if (info.offset.y > 120 || info.velocity.y > 700)
                  toggleNotification();
              }}
            >
              {/* drag handle */}
              <div className="flex items-center justify-center pt-2">
                <div className="h-1.5 w-12 rounded-full bg-gray-300" />
              </div>

              {/* Header */}
              <div className="flex min-h-[56px] items-center justify-between border-b px-4 py-3 md:min-h-[64px] md:px-6">
                <h2 className="font-sf-semibold text-lg text-[#056BDA] md:text-xl">
                  Thông báo
                </h2>
                <button
                  onClick={toggleNotification}
                  className="rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <img
                    src="/icons/x-close.svg"
                    alt="Close"
                    className="size-6 md:size-7"
                  />
                </button>
              </div>

              {/* List: set chiều cao theo sheet */}
              <InfiniteScroll
                dataLength={noti.length}
                next={fetchNoti}
                hasMore={hasMore}
                loader={<p className="py-4 text-center">Đang tải thêm...</p>}
                endMessage={
                  <p className="py-4 text-center">Không còn thông báo</p>
                }
                // 85vh (sheet) - header(≈64px) - handle(≈12px) - padding nhỏ
                height={"calc(100vh - 76px)"}
              >
                <ul className="max-h-[calc(100%-270px)] overflow-auto bg-red">
                  {noti.map((data) => (
                    <li
                      key={data._id}
                      className={`px-4 md:px-6 ${
                        data.status === "Read" ? "bg-[#f9f9f9]" : "bg-blue-50"
                      }`}
                    >
                      <button
                        className="flex w-full items-center justify-start gap-3 border-b py-4 text-left transition-colors hover:bg-blue-100 md:py-6"
                        title={data.message}
                        onClick={() =>
                          (window.location.href = `/quan-ly-ho-so/ho-so/${data.profileId}`)
                        }
                      >
                        <img
                          src={data.icon}
                          alt=""
                          className="mr-1 h-7 w-7 md:h-8 md:w-8"
                          loading="lazy"
                        />
                        <p
                          className="line-clamp-2 text-sm md:text-base font-sf-regular"
                          dangerouslySetInnerHTML={{ __html: data.note }}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </InfiniteScroll>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
